import * as THREE from 'three';
import type { PropInstance } from '../zones/types';
import type { AssetLibrary } from './assets';
import { applyMergedChunks } from './mergedMaterial';
import type { QualityProfile } from './RenderContext';
import type { LightSource } from './vfx/PropLights';

/** Chunk size (m): instances are grouped per model and per chunk so off-screen chunks are culled. */
const CELL = 24;

/** Foliage that sways in the wind: model id prefix → top sway amplitude (m). */
const WIND: [RegExp, number][] = [
  [/^tree_(oak|pine|palm)/, 0.16],
  [/^tree_pine_snow/, 0.1],
  [/^tree_dead/, 0.06],
  [/^(bush|bush_flower)$/, 0.06],
  [/^(grass_tuft|reeds)$/, 0.09],
];

interface PropChunk {
  mesh: THREE.InstancedMesh;
  /** Tallest instance (m): small clutter stops casting shadows on lower presets. */
  height: number;
  noShadow: boolean;
}

/**
 * Static scenery rendered with InstancedMeshes per (model, sub-mesh, 24 m
 * chunk): a forest costs a few draw calls per visible chunk, and chunks
 * outside the camera (and shadow) frustum are culled whole. Also extracts the
 * world-space light/fire sockets of every placed prop.
 */
export class PropField {
  readonly group = new THREE.Group();
  /** Light and fire sockets of all placed props (world space). */
  readonly sockets: LightSource[] = [];
  private chunks: PropChunk[] = [];
  private windMaterials: THREE.Material[] = [];
  private readonly wind = { value: 0 };

  async build(props: PropInstance[], lib: AssetLibrary, heightAt: (x: number, z: number) => number): Promise<void> {
    const byModel = new Map<string, PropInstance[]>();
    for (const p of props) {
      const list = byModel.get(p.model);
      if (list) list.push(p);
      else byModel.set(p.model, [p]);
    }
    const tmp = new THREE.Matrix4();
    const inst = new THREE.Matrix4();
    const rot = new THREE.Quaternion();
    const pos = new THREE.Vector3();
    const scl = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);
    const sp = new THREE.Vector3();
    await Promise.all(
      [...byModel.entries()].map(async ([model, list]) => {
        const id = lib.resolve(model);
        if (!id) return; // Unknown prop: collide but don't draw.
        const t = await lib.load(id);
        if (t.placeholder) return;
        t.scene.updateMatrixWorld(true);
        const parts: { mesh: THREE.Mesh; local: THREE.Matrix4 }[] = [];
        t.scene.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) parts.push({ mesh: m, local: m.matrixWorld.clone() });
        });
        const modelH = t.entry?.height ?? 1;
        const windAmp = WIND.find(([re]) => re.test(id))?.[1] ?? 0;
        // Sockets (braziers, lamps…) in world space.
        for (const p of list) {
          if (t.sockets.length === 0) break;
          const s = p.scale ?? 1;
          inst.compose(pos.set(p.x, heightAt(p.x, p.z) + (p.y ?? 0), p.z), rot.setFromAxisAngle(up, p.rot ?? 0), scl.set(s, s, s));
          for (const k of t.sockets) {
            sp.copy(k.pos).applyMatrix4(inst);
            this.sockets.push({ kind: k.kind, pos: sp.clone(), color: k.color.clone(), intensity: k.intensity, distance: k.distance * s, ground: heightAt(sp.x, sp.z) });
          }
        }
        // Chunk the instances.
        const cells = new Map<string, PropInstance[]>();
        for (const p of list) {
          const key = `${Math.floor(p.x / CELL)},${Math.floor(p.z / CELL)}`;
          const c = cells.get(key);
          if (c) c.push(p);
          else cells.set(key, [p]);
        }
        for (const part of parts) {
          const material = windAmp > 0 ? this.windMaterial(part.mesh.material, windAmp / Math.max(1e-3, new THREE.Vector3().setFromMatrixScale(part.local).y)) : part.mesh.material;
          for (const cell of cells.values()) {
            const mesh = new THREE.InstancedMesh(part.mesh.geometry, material, cell.length);
            mesh.name = `props_${model}`;
            let tall = 0;
            cell.forEach((p, i) => {
              const s = p.scale ?? 1;
              pos.set(p.x, heightAt(p.x, p.z) + (p.y ?? 0), p.z);
              rot.setFromAxisAngle(up, p.rot ?? 0);
              scl.set(s, s, s);
              tmp.compose(pos, rot, scl).multiply(part.local);
              mesh.setMatrixAt(i, tmp);
              if (p.tint !== undefined) mesh.setColorAt(i, new THREE.Color(p.tint));
              tall = Math.max(tall, modelH * s);
            });
            mesh.receiveShadow = true;
            mesh.computeBoundingSphere();
            // Static scenery: no per-frame matrix recomposition.
            mesh.matrixAutoUpdate = false;
            mesh.updateMatrix();
            this.chunks.push({ mesh, height: tall, noShadow: cell.every((p) => p.noShadow) });
            this.group.add(mesh);
          }
        }
      }),
    );
  }

  /** Clone of a prop material with a wind sway in the vertex shader (quantized model space: y in -1..1). */
  private windMaterial(src: THREE.Material | THREE.Material[], amp: number): THREE.Material | THREE.Material[] {
    const one = (m: THREE.Material): THREE.Material => {
      const c = m.clone();
      const wind = this.wind;
      const merged = c.userData.merged === true;
      c.onBeforeCompile = (s) => {
        if (merged) applyMergedChunks(s);
        s.uniforms.uWindT = wind;
        s.uniforms.uWindAmp = { value: amp };
        s.vertexShader = s.vertexShader.replace('#include <common>', '#include <common>\nuniform float uWindT;\nuniform float uWindAmp;').replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          {
            vec3 afOrigin = vec3(0.0);
            #ifdef USE_INSTANCING
              afOrigin = (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
            #endif
            float afH = clamp(transformed.y * 0.5 + 0.5, 0.0, 1.0);
            float afPh = afOrigin.x * 0.21 + afOrigin.z * 0.17;
            float afGust = 0.75 + 0.25 * sin(uWindT * 0.37 + afOrigin.x * 0.02);
            float afBend = afH * afH * uWindAmp * afGust;
            transformed.x += sin(uWindT * 1.35 + afPh) * afBend;
            transformed.z += cos(uWindT * 1.07 + afPh * 1.3) * afBend * 0.6;
          }`,
        );
      };
      c.customProgramCacheKey = () => (merged ? 'af-wind-m' : 'af-wind');
      this.windMaterials.push(c);
      return c;
    };
    return Array.isArray(src) ? src.map(one) : one(src);
  }

  /** Shadow casting per quality preset (small clutter stops casting on lower presets). */
  applyQuality(q: QualityProfile): void {
    for (const c of this.chunks) c.mesh.castShadow = q.shadows && !c.noShadow && c.height >= q.propShadowMinHeight;
  }

  update(dt: number): void {
    this.wind.value += dt;
  }

  /** Instanced meshes (for stats). */
  get meshCount(): number {
    return this.chunks.length;
  }

  dispose(): void {
    for (const c of this.chunks) c.mesh.dispose();
    for (const m of this.windMaterials) m.dispose();
    this.chunks = [];
    this.windMaterials = [];
    this.group.clear();
  }
}
